import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Heart, Sparkles } from "lucide-react";
import { Footer } from "@/components/Footer";
import { POSTS, C, PostImage, blockText } from "@/pages/Blog";
import { SITE, usePageMeta } from "@/lib/seo";

const articleJsonLd = (p) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: p.title,
  description: p.excerpt,
  url: `${SITE}/observatoire/${p.slug}`,
  mainEntityOfPage: `${SITE}/observatoire/${p.slug}`,
  inLanguage: "fr-FR",
  datePublished: p.dateISO,
  dateModified: p.dateISO,
  ...(p.image ? { image: p.image } : {}),
  articleBody: p.content.map(blockText).join("\n\n"),
  author: { "@type": "Person", name: "Lucie" },
  publisher: { "@type": "Organization", name: "La Petite Boîte Intuitive", url: `${SITE}/` },
  isPartOf: { "@id": `${SITE}/observatoire#blog` },
});

const Block = ({ block }) => {
  if (typeof block === "string") {
    return <p className="font-body text-base leading-relaxed mb-5" style={{ color: C.body }}>{block}</p>;
  }
  if (block.h) {
    return <h2 className="font-display text-2xl sm:text-3xl font-light mt-12 mb-5" style={{ color: C.title }}>{block.h}</h2>;
  }
  return (
    <p className="font-body text-base leading-relaxed mb-5 pl-5 border-l" style={{ color: C.body, borderColor: "rgba(230,198,135,0.4)" }}>
      <strong className="font-medium" style={{ color: C.goldLine }}>{block.b}</strong> : {block.t}
    </p>
  );
};

const Article = ({ post }) => {
  usePageMeta({
    title: `${post.title} · L'Observatoire des Possibles`,
    description: post.excerpt,
    path: `/observatoire/${post.slug}`,
  });
  const others = POSTS.filter((p) => p.slug !== post.slug);

  return (
    <div className="relative min-h-screen flex flex-col" style={{ backgroundColor: C.bg }}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd(post)) }} />
      <div className="oracle-bg" />
      <div className="relative z-10 flex-1 max-w-3xl w-full mx-auto px-6 py-14 sm:py-20">
        <nav aria-label="Fil d'Ariane" className="mb-12">
          <Link
            to="/observatoire"
            data-testid="article-back-link"
            className="inline-flex items-center gap-2 font-body text-xs tracking-[0.12em] uppercase transition-colors hover:text-[#E6C687]"
            style={{ color: C.mist }}
          >
            <ArrowLeft size={14} strokeWidth={1.6} />
            L'Observatoire des Possibles
          </Link>
        </nav>

        <motion.article data-testid="blog-article" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          {post.motif === "heart" && (
            <div className="flex items-center justify-center gap-3 mb-8">
              <span className="h-px w-12" style={{ background: "linear-gradient(to right, transparent, rgba(230,198,135,0.6))" }} />
              <Heart size={24} strokeWidth={1.3} style={{ color: C.goldLine }} />
              <span className="h-px w-12" style={{ background: "linear-gradient(to left, transparent, rgba(230,198,135,0.6))" }} />
            </div>
          )}
          <PostImage post={post} testid="article-image" />
          {post.date && (
            <p className="font-body uppercase tracking-[0.14em] text-[11px] mb-3" style={{ color: C.goldLine }}>{post.date}</p>
          )}
          <h1 data-testid="article-title" className="font-display text-4xl sm:text-5xl font-light leading-tight mb-5" style={{ color: C.title }}>
            {post.title}
          </h1>
          <p className="font-display italic text-xl mb-10" style={{ color: C.mist }}>{post.excerpt}</p>
          {post.content.map((block, j) => <Block key={j} block={block} />)}
          <p className="font-display italic text-lg mt-8" style={{ color: C.goldLine }}>— Lucie</p>
        </motion.article>

        <div className="mt-14 rounded-2xl border p-8 text-center" style={{ borderColor: "rgba(230,198,135,0.22)", backgroundColor: "rgba(255,255,255,0.025)" }}>
          <p className="font-display text-2xl mb-5" style={{ color: C.title }}>Et toi, que te disent les lames ?</p>
          <Link
            to="/tirage"
            data-testid="article-cta-tirage"
            className="inline-flex items-center gap-2 rounded-full px-7 py-3 font-body text-xs uppercase tracking-[0.16em] transition-transform hover:-translate-y-0.5"
            style={{ backgroundColor: C.goldMat, color: C.bg }}
          >
            <Sparkles size={14} strokeWidth={1.6} />
            Faire mon tirage gratuit
          </Link>
        </div>

        {others.length > 0 && (
          <div className="mt-14" data-testid="article-others">
            <p className="font-body uppercase tracking-[0.3em] text-xs mb-5" style={{ color: C.mist }}>À lire aussi</p>
            <ul className="space-y-3">
              {others.map((p) => (
                <li key={p.slug}>
                  <Link
                    to={`/observatoire/${p.slug}`}
                    data-testid={`article-other-${p.slug}`}
                    className="font-display text-xl transition-colors hover:text-[#E6C687]"
                    style={{ color: C.title }}
                  >
                    {p.title} →
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default function BlogPost() {
  const { slug } = useParams();
  const post = POSTS.find((p) => p.slug === slug);
  if (!post) return <Navigate to="/observatoire" replace />;
  return <Article post={post} />;
}
